import type React from 'react'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Divider from '@mui/material/Divider'
import Tooltip from '@mui/material/Tooltip'
import { NavLink } from 'react-router-dom'
import { useFeatureFlags } from '../providers/FeatureFlagContext'
import { primaryNav, secondaryNav, type NavItem } from './navItems'

interface SidebarProps {
  /** Icon-rail mode: hide labels, show them as tooltips. */
  collapsed?: boolean
  /** Called after navigating — used to close the mobile drawer. */
  onNavigate?: () => void
}

const activeSx = {
  '&.active': {
    color: 'primary.main',
    bgcolor: 'action.selected',
    '& .MuiListItemIcon-root': { color: 'primary.main' },
  },
}

function NavRow({
  item,
  enabled,
  collapsed,
  onNavigate,
}: {
  item: NavItem
  enabled: boolean
  collapsed: boolean
  onNavigate?: () => void
}): JSX.Element {
  const Icon = item.icon
  const labelEl = collapsed ? null : <ListItemText primary={item.label} />

  if (!enabled) {
    return (
      <Tooltip title="Feature not yet available" placement="right">
        <span tabIndex={0}>
          <ListItemButton disabled>
            <ListItemIcon>
              <Icon />
            </ListItemIcon>
            {labelEl}
          </ListItemButton>
        </span>
      </Tooltip>
    )
  }

  const button = (
    <ListItemButton
      component={NavLink as React.ElementType}
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      sx={activeSx}
    >
      <ListItemIcon>
        <Icon />
      </ListItemIcon>
      {labelEl}
    </ListItemButton>
  )

  return collapsed ? (
    <Tooltip title={item.label} placement="right">
      {button}
    </Tooltip>
  ) : (
    button
  )
}

export const Sidebar: React.FC<SidebarProps> = ({
  collapsed = false,
  onNavigate,
}) => {
  const flags = useFeatureFlags()
  const isEnabled = (item: NavItem): boolean =>
    item.flag == null || Boolean(flags[item.flag])

  return (
    <List component="nav" aria-label="Sidebar" sx={{ py: 1 }}>
      {primaryNav.map((item) => (
        <NavRow
          key={item.to}
          item={item}
          enabled={isEnabled(item)}
          collapsed={collapsed}
          onNavigate={onNavigate}
        />
      ))}
      <Divider sx={{ my: 1 }} />
      {secondaryNav.map((item) => (
        <NavRow
          key={item.to}
          item={item}
          enabled={isEnabled(item)}
          collapsed={collapsed}
          onNavigate={onNavigate}
        />
      ))}
    </List>
  )
}
